/**
 * BoardShareModal: Invite members, manage roles, generate invite links.
 *
 * Wired to the Seitiate engine workspace endpoints
 * (`/api/v1/workspaces/{thread_id}/...`). When `workspaceId` is null —
 * which happens for accounts whose board access comes from non-membership
 * authority (e.g. the Director claim) rather than `/auth/me.workspaces[]`
 * — invite controls are disabled and a clear message is shown.
 *
 * BOARD-SHARE-INVITE-FLOW-REPAIR-1.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  X,
  Copy,
  Check,
  UserPlus,
  Link2,
  Loader2,
  Crown,
  Shield,
  User,
  Eye,
  Bot,
  AlertCircle,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { workspaces } from '@/api/engine';
import type { ThreadParticipant, InviteLinkResponse } from '@/api/types';

const ROLE_CONFIG: Record<string, { icon: typeof Crown; label: string; color: string }> = {
  owner:  { icon: Crown,  label: 'Owner',  color: 'text-amber-400' },
  admin:  { icon: Shield, label: 'Admin',  color: 'text-purple-400' },
  member: { icon: User,   label: 'Member', color: 'text-blue-400' },
  viewer: { icon: Eye,    label: 'Viewer', color: 'text-gray-400' },
  agent:  { icon: Bot,    label: 'Agent',  color: 'text-emerald-400' },
};

interface BoardShareModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string | null;
}

export function BoardShareModal({ open, onClose, workspaceId }: BoardShareModalProps) {
  const [members, setMembers] = useState<ThreadParticipant[]>([]);
  const [loading, setLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('member');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<InviteLinkResponse | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [operatorKind, setOperatorKind] = useState<'famili' | 'agent'>('famili');
  const [operatorId, setOperatorId] = useState('');
  const [operatorDisplayName, setOperatorDisplayName] = useState('');
  const [operatorLoading, setOperatorLoading] = useState(false);
  const [operatorError, setOperatorError] = useState<string | null>(null);
  const [operatorSuccess, setOperatorSuccess] = useState<string | null>(null);

  const inviteDisabled = !workspaceId;

  // Reset transient state when modal closes or workspace changes.
  useEffect(() => {
    if (!open) return;
    setInviteError(null);
    setInviteSuccess(null);
    setLinkError(null);
    setMembersError(null);
    setOperatorError(null);
    setOperatorSuccess(null);
    setInviteLink(null);
  }, [open, workspaceId]);

  // Fetch members when modal opens (only when we have a workspace).
  useEffect(() => {
    if (!open || !workspaceId) {
      setMembers([]);
      return;
    }
    setLoading(true);
    setMembersError(null);
    workspaces.listMembers(workspaceId)
      .then(setMembers)
      .catch((err: unknown) => {
        setMembers([]);
        setMembersError(err instanceof Error ? err.message : 'Failed to load members');
      })
      .finally(() => setLoading(false));
  }, [open, workspaceId]);

  const handleInvite = useCallback(async () => {
    if (!workspaceId) return;
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    setInviteError(null);
    setInviteSuccess(null);
    try {
      const participant = await workspaces.inviteByEmail(workspaceId, email, inviteRole);
      setMembers((prev) => [...prev, participant]);
      setInviteEmail('');
      setInviteSuccess(`Invited ${email}`);
      setTimeout(() => setInviteSuccess(null), 3000);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Invite failed');
    } finally {
      setInviting(false);
    }
  }, [workspaceId, inviteEmail, inviteRole]);

  const handleGenerateLink = useCallback(async () => {
    if (!workspaceId) return;
    setLinkLoading(true);
    setLinkError(null);
    try {
      const link = await workspaces.createInviteLink(workspaceId, inviteRole);
      setInviteLink(link);
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to generate invite link');
    } finally {
      setLinkLoading(false);
    }
  }, [workspaceId, inviteRole]);

  const handleCopyLink = useCallback(async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink.invite_url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  }, [inviteLink]);

  const handleAddOperator = useCallback(async () => {
    if (!workspaceId) return;
    const id = operatorId.trim();
    const displayName = operatorDisplayName.trim();
    if (!id) return;
    setOperatorLoading(true);
    setOperatorError(null);
    setOperatorSuccess(null);
    try {
      const participant = operatorKind === 'famili'
        ? await workspaces.addBondedFamili(workspaceId, id, displayName || undefined)
        : await workspaces.addDelegatedAgent(workspaceId, id, displayName || undefined);
      setMembers((prev) => {
        const existingIndex = prev.findIndex((member) => member.id === participant.id);
        if (existingIndex === -1) return [...prev, participant];
        return prev.map((member, index) => index === existingIndex ? participant : member);
      });
      setOperatorId('');
      setOperatorDisplayName('');
      setOperatorSuccess(`Added ${displayName || id}`);
      setTimeout(() => setOperatorSuccess(null), 3000);
    } catch (err) {
      setOperatorError(err instanceof Error ? err.message : 'Operator add failed');
    } finally {
      setOperatorLoading(false);
    }
  }, [workspaceId, operatorId, operatorDisplayName, operatorKind]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 z-50"
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        data-testid="board-share-modal"
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[440px] max-w-[90vw] max-h-[80vh] overflow-hidden rounded-xl shadow-2xl"
        style={{
          backgroundColor: 'var(--theme-surface-secondary, #1a1f2a)',
          borderColor: 'var(--theme-surface-tertiary, #222836)',
          border: '1px solid var(--theme-surface-tertiary, #222836)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3.5 border-b"
          style={{ borderColor: 'var(--theme-surface-tertiary, #222836)' }}
        >
          <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-text-primary, #f4f7fb)' }}>
            Share Board
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/5 transition-colors">
            <X className="w-4 h-4" style={{ color: 'var(--theme-text-muted, #8e98ab)' }} />
          </button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto max-h-[60vh]">
          {/* No-workspace explainer (shown when controls are disabled) */}
          {inviteDisabled && (
            <div
              data-testid="board-share-no-workspace"
              role="status"
              className="flex items-start gap-2 rounded-lg px-3 py-2.5 text-[11px]"
              style={{
                backgroundColor: 'var(--theme-surface-tertiary, #222836)',
                color: 'var(--theme-text-secondary, #c2c9d6)',
              }}
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
              <div>
                You can view this board through Director or task-read authority,
                but you are not a member of a workspace yet, so invites cannot be
                created from this account. Ask an admin to add you to the board
                workspace, or run the Board Share flow from an account that holds
                board membership.
              </div>
            </div>
          )}

          {/* Invite by email */}
          <div>
            <label className="text-[10px] font-mono uppercase mb-2 block" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
              <UserPlus className="w-3 h-3 inline mr-1" /> Invite by Email
            </label>
            <div className="flex items-center gap-2">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void handleInvite(); }}
                placeholder="email@example.com"
                disabled={inviteDisabled}
                aria-label="Invite email"
                className="flex-1 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[var(--sp-accent)]/50 disabled:opacity-40"
                style={{
                  backgroundColor: 'var(--theme-surface-tertiary, #222836)',
                  color: 'var(--theme-text-primary, #f4f7fb)',
                }}
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                disabled={inviteDisabled}
                aria-label="Invite role"
                className="rounded-lg px-2 py-2 text-xs font-mono disabled:opacity-40"
                style={{
                  backgroundColor: 'var(--theme-surface-tertiary, #222836)',
                  color: 'var(--theme-text-primary, #f4f7fb)',
                }}
              >
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
              </select>
              <button
                onClick={() => void handleInvite()}
                disabled={inviteDisabled || !inviteEmail.trim() || inviting}
                data-testid="board-share-invite-button"
                className="px-3 py-2 rounded-lg text-xs font-bold disabled:opacity-40 transition-colors"
                style={{
                  backgroundColor: 'var(--theme-accent-primary, var(--sp-accent))',
                  color: 'var(--theme-surface-primary, #0f1419)',
                }}
              >
                {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Invite'}
              </button>
            </div>
            {inviteError && <p className="text-[11px] text-red-400 mt-1">{inviteError}</p>}
            {inviteSuccess && <p className="text-[11px] text-green-400 mt-1">{inviteSuccess}</p>}
          </div>

          {/* Invite link */}
          <div>
            <label className="text-[10px] font-mono uppercase mb-2 block" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
              <Link2 className="w-3 h-3 inline mr-1" /> Invite Link
            </label>
            {inviteLink ? (
              <div className="flex items-center gap-2">
                <div
                  data-testid="board-share-invite-link"
                  className="flex-1 rounded-lg px-3 py-2 text-xs font-mono truncate"
                  style={{
                    backgroundColor: 'var(--theme-surface-tertiary, #222836)',
                    color: 'var(--theme-text-secondary, #c2c9d6)',
                  }}
                >
                  {inviteLink.invite_url}
                </div>
                <button
                  onClick={() => void handleCopyLink()}
                  className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                  aria-label="Copy invite link"
                >
                  {linkCopied
                    ? <Check className="w-3.5 h-3.5 text-green-400" />
                    : <Copy className="w-3.5 h-3.5" style={{ color: 'var(--theme-text-muted, #8e98ab)' }} />
                  }
                </button>
              </div>
            ) : (
              <button
                onClick={() => void handleGenerateLink()}
                disabled={inviteDisabled || linkLoading}
                data-testid="board-share-generate-link-button"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  borderColor: 'var(--theme-surface-tertiary, #222836)',
                  color: 'var(--theme-text-secondary, #c2c9d6)',
                }}
              >
                {linkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                Generate invite link
              </button>
            )}
            {linkError && <p className="text-[11px] text-red-400 mt-1">{linkError}</p>}
          </div>

          {/* Delegated operators */}
          <div>
            <label className="text-[10px] font-mono uppercase mb-2 block" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
              <Bot className="w-3 h-3 inline mr-1" /> Delegated Operators
            </label>
            <div className="grid grid-cols-[104px_1fr] gap-2">
              <select
                value={operatorKind}
                onChange={(e) => setOperatorKind(e.target.value as 'famili' | 'agent')}
                disabled={inviteDisabled}
                aria-label="Operator kind"
                className="rounded-lg px-2 py-2 text-xs font-mono disabled:opacity-40"
                style={{
                  backgroundColor: 'var(--theme-surface-tertiary, #222836)',
                  color: 'var(--theme-text-primary, #f4f7fb)',
                }}
              >
                <option value="famili">Bonded famili</option>
                <option value="agent">Agent</option>
              </select>
              <input
                type="text"
                value={operatorId}
                onChange={(e) => setOperatorId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void handleAddOperator(); }}
                placeholder={operatorKind === 'famili' ? 'coo' : 'codex'}
                disabled={inviteDisabled}
                aria-label={operatorKind === 'famili' ? 'Famili name' : 'Agent id'}
                className="rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[var(--sp-accent)]/50 disabled:opacity-40"
                style={{
                  backgroundColor: 'var(--theme-surface-tertiary, #222836)',
                  color: 'var(--theme-text-primary, #f4f7fb)',
                }}
              />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="text"
                value={operatorDisplayName}
                onChange={(e) => setOperatorDisplayName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void handleAddOperator(); }}
                placeholder="Display name"
                disabled={inviteDisabled}
                aria-label="Operator display name"
                className="flex-1 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[var(--sp-accent)]/50 disabled:opacity-40"
                style={{
                  backgroundColor: 'var(--theme-surface-tertiary, #222836)',
                  color: 'var(--theme-text-primary, #f4f7fb)',
                }}
              />
              <button
                onClick={() => void handleAddOperator()}
                disabled={inviteDisabled || !operatorId.trim() || operatorLoading}
                data-testid="board-share-add-operator-button"
                aria-label="Add delegated operator"
                aria-busy={operatorLoading}
                className="px-3 py-2 rounded-lg text-xs font-bold disabled:opacity-40 transition-colors"
                style={{
                  backgroundColor: 'var(--theme-accent-primary, var(--sp-accent))',
                  color: 'var(--theme-surface-primary, #0f1419)',
                }}
              >
                {operatorLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}
              </button>
            </div>
            {operatorError && <p role="alert" className="text-[11px] text-red-400 mt-1">{operatorError}</p>}
            {operatorSuccess && <p role="status" className="text-[11px] text-green-400 mt-1">{operatorSuccess}</p>}
          </div>

          {/* Members list */}
          <div>
            <label className="text-[10px] font-mono uppercase mb-2 block" style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
              Members ({members.length})
            </label>
            {loading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
              </div>
            ) : membersError ? (
              <p className="text-[11px] text-red-400 italic py-3 text-center">{membersError}</p>
            ) : members.length === 0 ? (
              <p className="text-xs text-gray-600 italic py-3 text-center">
                {inviteDisabled
                  ? 'Members cannot be loaded without a workspace.'
                  : 'No members yet. Send an invite to get started.'}
              </p>
            ) : (
              <div className="space-y-1">
                {members.map((m) => {
                  const roleConfig = ROLE_CONFIG[m.participant_role] ?? ROLE_CONFIG.member;
                  const RoleIcon = roleConfig.icon;
                  const displayName = m.display_name_snapshot ?? m.agent_id ?? m.user_id ?? 'Unknown';
                  const initial = m.agent_id ? 'A' : (m.display_name_snapshot ?? '?')[0]?.toUpperCase() ?? '?';
                  return (
                    <div
                      key={m.id}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg"
                      style={{ backgroundColor: 'var(--theme-surface-tertiary, #222836)' }}
                    >
                      <div className="w-7 h-7 rounded-full bg-blue-500/15 flex items-center justify-center text-[10px] font-bold text-blue-400 shrink-0">
                        {initial}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate" style={{ color: 'var(--theme-text-primary, #f4f7fb)' }}>
                          {displayName}
                        </div>
                        {m.joined_at && (
                          <div className="text-[10px] text-gray-500">
                            Joined {new Date(m.joined_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <span className={`flex items-center gap-1 text-[10px] font-mono ${roleConfig.color}`}>
                        <RoleIcon className="w-3 h-3" />
                        {roleConfig.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
}
